/**
 * Bluetooth Printer Service using Web Bluetooth API
 * Supports Bluetooth thermal printers (RPP02N, bp-td110bt, etc.)
 */

import type { ReceiptData } from './thermalPrinterService';

// ESC/POS Commands
const ESC = '\x1B';
const GS = '\x1D';

// Bluetooth Service UUIDs for ESC/POS printers
const PRINTER_SERVICE_UUID = '000018f0-0000-1000-8000-00805f9b34fb'; // Generic Printer Service
const PRINTER_CHAR_UUID = '00002af1-0000-1000-8000-00805f9b34fb';    // Printer Characteristic

export class BluetoothPrinterService {
  private device: BluetoothDevice | null = null;
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private server: BluetoothRemoteGATTServer | null = null;

  /**
   * Request Bluetooth printer access from user
   */
  async requestPrinter(): Promise<void> {
    try {
      // Check Bluetooth availability
      if (!navigator.bluetooth) {
        throw new Error('Web Bluetooth API tidak tersedia di browser ini. Gunakan Chrome/Edge terbaru.');
      }

      // Request device with filters for thermal printers
      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          { services: [PRINTER_SERVICE_UUID] },
          { name: 'RPP02N' },
          { name: 'bp-td110bt' },
          { namePrefix: 'RPP' },
          { namePrefix: 'BP-' },
          { namePrefix: 'MTP-' },
          { namePrefix: 'Printer' },
        ],
        optionalServices: [PRINTER_SERVICE_UUID]
      });

      // Connect to GATT server (reconnect if disconnected)
      if (!this.device.gatt!.connected) {
        this.server = await this.device.gatt!.connect();
      } else {
        this.server = this.device.gatt!;
      }
      
      // Get printer service
      const service = await this.server.getPrimaryService(PRINTER_SERVICE_UUID);
      
      // Get printer characteristic
      this.characteristic = await service.getCharacteristic(PRINTER_CHAR_UUID);

      console.log('✅ Bluetooth printer connected:', this.device.name);
    } catch (error: any) {
      console.error('❌ Bluetooth connection failed:', error);
      
      if (error.name === 'NotFoundError') {
        throw new Error('Tidak ada printer dipilih. Silakan pilih printer Bluetooth dari daftar.');
      } else if (error.message?.includes('Bluetooth')) {
        throw new Error('Bluetooth tidak tersedia. Aktifkan Bluetooth dan coba lagi.');
      } else {
        throw new Error('Gagal terhubung ke printer Bluetooth: ' + error.message);
      }
    }
  }

  /**
   * Auto-connect to previously paired printer
   */
  async autoConnect(): Promise<boolean> {
    try {
      const devices = await navigator.bluetooth.getDevices();
      if (devices.length === 0) return false;

      // Try to connect to first available printer
      for (const device of devices) {
        try {
          this.device = device;
          this.server = await device.gatt!.connect();
          const service = await this.server.getPrimaryService(PRINTER_SERVICE_UUID);
          this.characteristic = await service.getCharacteristic(PRINTER_CHAR_UUID);
          
          console.log('✅ Auto-connected to Bluetooth printer:', device.name);
          return true;
        } catch (error) {
          console.warn('Failed to connect to', device.name, error);
          continue;
        }
      }

      return false;
    } catch (error) {
      console.warn('Auto-connect failed:', error);
      return false;
    }
  }

  /**
   * Send raw data to printer
   */
  private async send(data: string): Promise<void> {
    if (!this.characteristic) {
      throw new Error('Printer not connected');
    }

    const encoder = new TextEncoder();
    const bytes = encoder.encode(data);
    
    // Split into chunks if needed (max 512 bytes per write for most BLE devices)
    const chunkSize = 512;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.slice(i, i + chunkSize);
      await this.characteristic.writeValue(chunk);
      
      // Small delay between chunks
      if (i + chunkSize < bytes.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
  }

  /**
   * Print receipt with ESC/POS commands
   */
  async printReceipt(data: ReceiptData): Promise<void> {
    // Reconnect if disconnected
    if (this.device && !this.device.gatt!.connected) {
      console.log('🔄 Reconnecting Bluetooth printer...');
      this.server = await this.device.gatt!.connect();
      const service = await this.server.getPrimaryService(PRINTER_SERVICE_UUID);
      this.characteristic = await service.getCharacteristic(PRINTER_CHAR_UUID);
    }

    if (!this.characteristic) {
      throw new Error('Printer not connected. Please connect first.');
    }

    let receipt = '';

    // Detect paper width (default 80mm)
    const paperWidth = (data as any).paperWidth || 80;
    const maxChars = paperWidth === 58 ? 32 : 48; // Characters per line
    const separator = '='.repeat(maxChars);
    const itemNameMaxLen = paperWidth === 58 ? 28 : 44; // Item name max length

    // Initialize printer
    receipt += ESC + '@'; // Reset printer
    
    // Font selection: 58mm uses Font B (smaller for narrow paper), 80mm uses Font A (normal)
    if (paperWidth === 58) {
      receipt += ESC + 'M' + '\x01'; // Font B (9x17 small) for 58mm narrow paper
    } else {
      receipt += ESC + 'M' + '\x00'; // Font A (12x24 normal) for 80mm wide paper
    }

    // Header - Center align
    receipt += ESC + 'a' + '\x01'; // Center align
    
    // Use custom header from backend or fallback to default
    const headerLines = ((data as any).headerText || `${data.branchName}\nSparepart & Accessories`).split('\n');
    for (let i = 0; i < headerLines.length; i++) {
      if (i === 0) {
        receipt += ESC + '!' + '\x30'; // Double height & width for first line
        receipt += headerLines[i] + '\n';
        receipt += ESC + '!' + '\x00'; // Normal size
      } else {
        receipt += headerLines[i] + '\n';
      }
    }
    receipt += separator + '\n';

    // Transaction info - Left align
    receipt += ESC + 'a' + '\x00'; // Left align
    
    if (paperWidth === 58) {
      // Compact format for 58mm
      receipt += `${data.invoiceNumber}\n`;
      receipt += `${data.date}\n`;
      receipt += `${data.cashier}\n`;
    } else {
      // Full format for 80mm
      receipt += `No: ${data.invoiceNumber}\n`;
      receipt += `Tanggal: ${data.date}\n`;
      receipt += `Kasir: ${data.cashier}\n`;
    }

    // Customer info
    if (data.customer) {
      if (paperWidth === 58) {
        receipt += `${data.customer.name}\n`;
        receipt += `${data.customer.phone}\n`;
        receipt += `${data.customer.tier}\n`;
      } else {
        receipt += `Customer: ${data.customer.name}\n`;
        receipt += `HP: ${data.customer.phone}\n`;
        receipt += `Tier: ${data.customer.tier}\n`;
      }
    } else {
      if (paperWidth === 58) {
        receipt += `Pelanggan\n`;
      } else {
        receipt += `Customer: Pelanggan\n`;
      }
    }

    receipt += separator + '\n';

    // Items
    for (const item of data.items) {
      const itemName = (item.name || 'Unknown Item').length > itemNameMaxLen 
        ? (item.name || 'Unknown Item').substring(0, itemNameMaxLen - 3) + '...' 
        : (item.name || 'Unknown Item');
      receipt += `${itemName}\n`;
      
      if (paperWidth === 58) {
        // Compact format: "1 x 108.700 = 108.700"
        receipt += `${item.quantity} x ${item.price.toLocaleString('id-ID')}\n`;
        receipt += `= ${item.subtotal.toLocaleString('id-ID')}\n`;
      } else {
        // Full format with spacing
        receipt += `  ${item.quantity} x ${item.price.toLocaleString('id-ID')}`;
        receipt += ` = ${item.subtotal.toLocaleString('id-ID')}\n`;
      }
    }

    receipt += separator + '\n';

    // Totals - Right align
    receipt += ESC + 'a' + '\x02'; // Right align
    
    if (paperWidth === 58) {
      // Compact labels for 58mm
      receipt += `Sub: Rp ${data.subtotal.toLocaleString('id-ID')}\n`;
      
      if (data.markup && data.markup > 0) {
        const markupPct = ((data.markup / data.subtotal) * 100).toFixed(0);
        receipt += `+${markupPct}%: Rp ${data.markup.toLocaleString('id-ID')}\n`;
      }
      
      if (data.discount && data.discount > 0) {
        const discountPct = ((data.discount / data.subtotal) * 100).toFixed(0);
        receipt += `-${discountPct}%: Rp ${data.discount.toLocaleString('id-ID')}\n`;
      }
    } else {
      // Full labels for 80mm
      receipt += `Subtotal: Rp ${data.subtotal.toLocaleString('id-ID')}\n`;
      
      if (data.markup && data.markup > 0) {
        const markupPct = ((data.markup / data.subtotal) * 100).toFixed(0);
        receipt += `Markup (+${markupPct}%): Rp ${data.markup.toLocaleString('id-ID')}\n`;
      }
      
      if (data.discount && data.discount > 0) {
        const discountPct = ((data.discount / data.subtotal) * 100).toFixed(0);
        receipt += `Diskon (-${discountPct}%): Rp ${data.discount.toLocaleString('id-ID')}\n`;
      }
    }

    receipt += ESC + '!' + '\x20'; // Double height
    receipt += `TOTAL: Rp ${data.total.toLocaleString('id-ID')}\n`;
    receipt += ESC + '!' + '\x00'; // Normal size

    receipt += ESC + 'a' + '\x00'; // Left align
    receipt += separator + '\n';

    // Payment info
    const paymentLabels: Record<string, string> = {
      cash: 'TUNAI',
      tempo: 'TEMPO',
      qris: 'QRIS',
      transfer: 'TRANSFER',
    };
    
    if (paperWidth === 58) {
      receipt += `${paymentLabels[data.paymentMethod] || data.paymentMethod.toUpperCase()}\n`;
    } else {
      receipt += `Metode: ${paymentLabels[data.paymentMethod] || data.paymentMethod.toUpperCase()}\n`;
    }

    if (data.paymentMethod === 'cash' && data.paid) {
      if (paperWidth === 58) {
        receipt += `Byr: Rp ${data.paid.toLocaleString('id-ID')}\n`;
        receipt += `Kbl: Rp ${(data.change || 0).toLocaleString('id-ID')}\n`;
      } else {
        receipt += `Bayar: Rp ${data.paid.toLocaleString('id-ID')}\n`;
        receipt += `Kembali: Rp ${(data.change || 0).toLocaleString('id-ID')}\n`;
      }
    } else if (data.paymentMethod === 'tempo' && data.dueDate) {
      if (paperWidth === 58) {
        receipt += `Tempo: ${data.dueDate}\n`;
      } else {
        receipt += `Jatuh Tempo: ${data.dueDate}\n`;
      }
      receipt += ESC + '!' + '\x10'; // Bold
      receipt += `BELUM LUNAS\n`;
      receipt += ESC + '!' + '\x00'; // Normal
    }

    receipt += separator + '\n';

    // Footer - Center align
    receipt += ESC + 'a' + '\x01'; // Center align
    
    // Use custom footer from backend or fallback to default
    const defaultFooter = paperWidth === 58 
      ? 'Terima Kasih\nBarang dibeli\ntdk dpt tukar/kembali'
      : 'Terima Kasih\nBarang yang sudah dibeli\ntidak dapat ditukar/dikembalikan';
    const footerLines = ((data as any).footerText || defaultFooter).split('\n');
    for (const line of footerLines) {
      receipt += line + '\n';
    }
    // Spacing
    if ((data as any).continuousMode) {
      // Continuous mode: more spacing but no auto-cut
      receipt += '\n\n\n\n\n\n\n\n'; // 8 line feeds for manual tear
    } else {
      // Auto-cut mode: standard spacing + cut
      receipt += '\n\n\n\n\n'; // 5 line feeds
      receipt += GS + 'V' + '\x00'; // Full cut
      receipt += '\n\n\n'; // Extra feeds after cut
    }

    // Send to printer
    await this.send(receipt);
  }

  /**
   * Test print
   */
  async testPrint(): Promise<void> {
    let test = '';
    test += ESC + '@'; // Reset
    test += ESC + 'a' + '\x01'; // Center
    test += ESC + '!' + '\x30'; // Double size
    test += 'TEST PRINT\n';
    test += ESC + '!' + '\x00'; // Normal
    test += '================================\n';
    test += 'Bluetooth printer OK!\n';
    test += `${new Date().toLocaleString('id-ID')}\n`;
    test += '\n\n';
    test += GS + 'V' + '\x41' + '\x00'; // Cut

    await this.send(test);
  }

  /**
   * Disconnect printer
   */
  async disconnect(): Promise<void> {
    if (this.server) {
      try {
        await this.server.disconnect();
      } catch (error) {
        console.warn('Error disconnecting:', error);
      }
      this.server = null;
      this.characteristic = null;
      this.device = null;
      console.log('✅ Bluetooth printer disconnected');
    }
  }

  /**
   * Check if printer is connected
   */
  isConnected(): boolean {
    return this.server?.connected === true && this.characteristic !== null;
  }

  /**
   * Get printer info
   */
  getPrinterInfo(): string | null {
    return this.device?.name || null;
  }
}

// Singleton instance
export const bluetoothPrinter = new BluetoothPrinterService();
